"use client";

import { useEffect, useState } from "react";
import { useAgentOverlayContext } from "./AgentOverlayProvider";
import { JsonTree } from "./AgentJsonTree";
import type { PromptHistoryEntry, PromptTrigger } from "./useAgentOverlay";
import { SUPPRESSION_WINDOW_MS, STALL_THRESHOLD_MS } from "./triggers";

function secs(ms: number) {
  return `${(ms / 1000).toFixed(1)}s`;
}

function relativeLabel(ts: number, now: number): string {
  const delta = now - ts;
  if (delta < 1000) return "just now";
  return `${secs(delta)} ago`;
}

function triggerLabel(trigger: PromptTrigger): string {
  if (trigger === "user_message") return "user message";
  return trigger.replace(/_/g, " ");
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 font-mono text-[10px] leading-snug">
      <span className="w-14 flex-shrink-0" style={{ color: "var(--ink-faint)" }}>
        {label}
      </span>
      <span className="min-w-0">{value}</span>
    </div>
  );
}

type PayloadView = "history" | "live";

function historySummary(entry: PromptHistoryEntry): string {
  const msg = entry.payload.userMessage;
  if (msg) {
    const preview = msg.length > 28 ? `${msg.slice(0, 28)}…` : msg;
    return `"${preview}"`;
  }
  return triggerLabel(entry.trigger);
}

export function AgentChatDebugPanel() {
  const { state, debugRefs, getPromptPayload } = useAgentOverlayContext();
  const [now, setNow] = useState(0);
  const [view, setView] = useState<PayloadView>("history");
  const [userSelectedId, setUserSelectedId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  const history = debugRefs.promptHistory.current;
  const livePayload = getPromptPayload();
  const latestHistoryId = history.length > 0 ? history[history.length - 1].id : null;
  const selectedId =
    userSelectedId && history.some((entry) => entry.id === userSelectedId)
      ? userSelectedId
      : latestHistoryId;

  const selectedEntry =
    selectedId !== null ? history.find((entry) => entry.id === selectedId) : undefined;
  const displayedPayload = view === "live" ? livePayload : (selectedEntry?.payload ?? null);

  const { lastFiredAt, lastActivityAt, performance, pendingDelayUntil, events, lastStreamError } =
    debugRefs;

  const lastFired = lastFiredAt.current;
  const lastActivity = lastActivityAt.current;
  const perf = performance.current;
  const pendingUntil = pendingDelayUntil.current;
  const debugEvents = events.current;

  const suppressionRemaining =
    lastFired !== null ? Math.max(0, SUPPRESSION_WINDOW_MS - (now - lastFired)) : 0;
  const suppressed = suppressionRemaining > 0;

  const idleMs = now - lastActivity;
  const stallProgress = Math.min(idleMs / STALL_THRESHOLD_MS, 1);
  const stallFired = idleMs >= STALL_THRESHOLD_MS;
  const isStreaming = state.messages.some((m) => m.isStreaming);
  const streamError = lastStreamError.current;
  const selectedStreamError = selectedEntry?.streamError;

  async function copyPayload() {
    if (!displayedPayload) return;
    await navigator.clipboard.writeText(JSON.stringify(displayedPayload, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div
        className="flex-shrink-0 space-y-2 px-3 py-2"
        style={{ borderBottom: "1px solid var(--hairline)" }}
      >
        <Row
          label="suppress"
          value={
            suppressed ? (
              <span style={{ color: "#ff6b6b" }}>active · {secs(suppressionRemaining)} left</span>
            ) : (
              <span style={{ color: "#6bcb77" }}>ready</span>
            )
          }
        />
        <div>
          <Row
            label="stall"
            value={
              stallFired ? (
                <span style={{ color: "#ffd166" }}>fired · {secs(idleMs)} idle</span>
              ) : (
                <span>
                  {secs(idleMs)} / {secs(STALL_THRESHOLD_MS)}
                </span>
              )
            }
          />
          <div
            className="mt-1 h-0.5 overflow-hidden rounded-full"
            style={{ background: "var(--surface-sunk)" }}
          >
            <div
              className="h-full rounded-full transition-all duration-200"
              style={{
                width: `${stallProgress * 100}%`,
                background: stallFired ? "#ffd166" : stallProgress > 0.7 ? "#ff9f43" : "#6bcb77",
              }}
            />
          </div>
        </div>
        <Row
          label="pending"
          value={
            pendingUntil !== null ? (
              <span style={{ color: "#ffd166" }}>in {secs(Math.max(0, pendingUntil - now))}</span>
            ) : (
              <span style={{ color: "var(--ink-faint)" }}>-</span>
            )
          }
        />
        <Row
          label="stream"
          value={
            isStreaming ? (
              <span style={{ color: "#74c0fc" }}>streaming</span>
            ) : streamError ? (
              <span style={{ color: "#ff6b6b" }}>failed</span>
            ) : (
              <span style={{ color: "var(--ink-faint)" }}>idle</span>
            )
          }
        />
        {streamError ? (
          <Row
            label="error"
            value={
              <span className="break-all" style={{ color: "#ff6b6b" }}>
                {streamError}
              </span>
            }
          />
        ) : null}
        <Row
          label="perf"
          value={
            <span>
              <span style={{ color: "#6bcb77" }}>✓{perf.correct}</span>
              {" · "}
              <span style={{ color: "#ff6b6b" }}>✗{perf.incorrect}</span>
              {" · "}
              <span style={{ color: "#ffd166" }}>🔥{perf.streak}</span>
            </span>
          }
        />
        {lastFired !== null && <Row label="last fire" value={relativeLabel(lastFired, now)} />}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div
          className="flex flex-shrink-0 items-center justify-between gap-2 px-3 py-1.5"
          style={{ borderBottom: "1px solid var(--hairline)" }}
        >
          <div
            className="flex rounded-full p-0.5"
            style={{ background: "var(--surface-sunk)" }}
            role="tablist"
            aria-label="Payload view"
          >
            {(["history", "live"] as const).map((panel) => (
              <button
                key={panel}
                type="button"
                role="tab"
                aria-selected={view === panel}
                onClick={() => setView(panel)}
                className="rounded-full px-2 py-0.5 font-mono text-[10px] capitalize transition-colors"
                style={{
                  background: view === panel ? "var(--surface)" : "transparent",
                  color: view === panel ? "var(--ink-body)" : "var(--ink-faint)",
                  boxShadow: view === panel ? "var(--inset-field)" : "none",
                }}
              >
                {panel}
                {panel === "history" && history.length > 0 ? ` (${history.length})` : ""}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => void copyPayload()}
            disabled={!displayedPayload}
            className="rounded px-1.5 py-0.5 font-mono text-[10px] transition-colors hover:bg-black/5 disabled:opacity-40"
            style={{ color: copied ? "#6bcb77" : "var(--ink-faint)" }}
          >
            {copied ? "copied" : "copy JSON"}
          </button>
        </div>

        {view === "history" && (
          <div
            className="flex-shrink-0 overflow-x-auto px-3 py-1.5"
            style={{ borderBottom: "1px solid var(--hairline)" }}
          >
            {history.length === 0 ? (
              <p className="font-mono text-[10px]" style={{ color: "var(--ink-faint)" }}>
                No requests yet - trigger an event or send a message.
              </p>
            ) : (
              <ul className="flex gap-1.5">
                {[...history].reverse().map((entry, reverseIndex) => {
                  const index = history.length - 1 - reverseIndex;
                  const selected = entry.id === selectedId;
                  return (
                    <li key={entry.id} className="flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setUserSelectedId(entry.id)}
                        className="rounded-lg px-2 py-1 text-left font-mono text-[10px] leading-snug transition-colors"
                        style={{
                          background: selected ? "var(--coral-light)" : "var(--surface-sunk)",
                          color: selected ? "white" : "var(--ink-body)",
                          boxShadow: selected ? "none" : "var(--inset-field)",
                          maxWidth: "9rem",
                          outline: entry.streamError ? "1px solid #ff6b6b" : undefined,
                        }}
                        title={`#${index + 1} · ${triggerLabel(entry.trigger)} · ${relativeLabel(entry.ts, now)}`}
                      >
                        <span className="block font-semibold">#{index + 1}</span>
                        <span className="block truncate opacity-80">{historySummary(entry)}</span>
                        <span className="block opacity-60">{relativeLabel(entry.ts, now)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto px-3 py-2">
          {view === "live" ? (
            <>
              <p
                className="mb-2 font-mono text-[10px] leading-relaxed"
                style={{ color: "var(--ink-faint)" }}
              >
                Next request preview - reflects current lesson state before the next fire.
              </p>
              <JsonTree value={livePayload} name="body" defaultExpanded />
            </>
          ) : displayedPayload ? (
            <>
              {selectedEntry && (
                <p
                  className="mb-2 font-mono text-[10px] leading-relaxed"
                  style={{ color: "var(--ink-faint)" }}
                >
                  Sent {relativeLabel(selectedEntry.ts, now)} ·{" "}
                  {triggerLabel(selectedEntry.trigger)}
                  {selectedStreamError ? (
                    <>
                      {" · "}
                      <span style={{ color: "#ff6b6b" }}>stream failed</span>
                    </>
                  ) : null}
                </p>
              )}
              {selectedStreamError ? (
                <p
                  className="mb-2 break-all font-mono text-[10px] leading-relaxed"
                  style={{ color: "#ff6b6b" }}
                >
                  {selectedStreamError}
                </p>
              ) : null}
              <JsonTree value={displayedPayload} name="body" defaultExpanded />
            </>
          ) : (
            <p className="font-mono text-[10px]" style={{ color: "var(--ink-faint)" }}>
              Select a request above to inspect its payload.
            </p>
          )}
        </div>
      </div>

      {debugEvents.length > 0 && (
        <div className="flex-shrink-0" style={{ borderTop: "1px solid var(--hairline)" }}>
          <div
            className="px-3 pt-1.5 pb-0.5 font-mono text-[10px]"
            style={{ color: "var(--ink-faint)" }}
          >
            Events ({debugEvents.length})
          </div>
          <ul className="max-h-24 space-y-0.5 overflow-y-auto px-3 pb-2">
            {[...debugEvents]
              .reverse()
              .slice(0, 8)
              .map((ev, i) => (
                <li key={i} className="flex items-start gap-1.5 font-mono text-[10px]">
                  <span className="flex-shrink-0" style={{ color: "var(--ink-faint)" }}>
                    {relativeLabel(ev.ts, now)}
                  </span>
                  <span
                    className="min-w-0 break-all"
                    style={{
                      color: ev.suppressed
                        ? "#ff6b6b"
                        : ev.delayMs > 0
                          ? "#ffd166"
                          : "var(--ink-body)",
                    }}
                  >
                    {ev.type}
                    {ev.suppressed && " ↷suppressed"}
                    {!ev.suppressed && ev.delayMs > 0 && ` +${secs(ev.delayMs)}`}
                  </span>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
